import { AutoCompleteInputDataService } from './auto-complete-input.data';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { map, debounceTime, distinctUntilChanged, switchMap, tap, catchError } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { Component,  AfterViewInit, ViewChild, ElementRef, Input, Output, EventEmitter } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";

@Component({
    selector: "auto-complete-input",
    templateUrl: "auto-complete-input.tmpl.html",
    styleUrls: ["./auto-complete-input.scss"],
})
export class AutoCompleteInputComponent implements AfterViewInit {

    constructor(
        private data: AutoCompleteInputDataService
    ) {
    }


    noResults: boolean = false;
    loading: boolean = false;
    sourceList: string;
    filterValues: Observable<string>;
    public searchResults: string[] = [];
    public currentQuery: string = '';
    private lastCommittedValue: string = '';
    private isInputFocused: boolean = false;


    @ViewChild('txtSearch', { static: true }) txtSearch: ElementRef;
    @ViewChild("matAutocomplete") matAutocomplete: MatAutocomplete;
    @ViewChild(MatAutocompleteTrigger, { static: true }) matAutocompleteTrigger: MatAutocompleteTrigger;



    @Input("appearance") public appearance: string = "outline";
    @Input("leftToRight") public leftToRight: boolean = false;
    @Input("formGroupModel") public formGroupModel: FormGroup;
    @Input('stringValueControl') public stringValueControl: FormControl;
    @Input('controllerName') public controllerName: string;
    @Input('placeholder') placeholder?: string = '...';

    @Input('withLabel') public withLabel: boolean = true;
    @Output() onSelect = new EventEmitter<string | null>();

    public get canCreateCurrentValue(): boolean {
        const query = this.normalizeValue(this.currentQuery);
        if (!query) {
            return false;
        }

        return !this.searchResults.some(item => this.normalizeValue(item).toLowerCase() === query.toLowerCase());
    }

    public itemSelect(item) {
        const value = this.normalizeValue(item);
        if (!value) {
            if (this.lastCommittedValue) {
                this.lastCommittedValue = '';
                this.activeItem = null;
                this.onSelect.emit(null);
            }
            return false;
        }

        this.activeItem = value;
        if (this.stringValueControl.value !== value) {
            this.stringValueControl.setValue(value, { emitEvent: false });
        }

        if (this.lastCommittedValue !== value) {
            this.lastCommittedValue = value;
            this.onSelect.emit(value);
        }

        return false;
    }

    isAutofilled;
    autofilled(e) {
        //     console.log("autofilled",e);
    }
    public activeItem: string;
    public isActive(value): boolean {
        return (value === this.activeItem);
    }

    public setActive(event, item): void {
        this.itemSelect(item);
    }

    public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        this.itemSelect(event.option.value);
    }

    public handleFocus(): void {
        this.isInputFocused = true;
        const query = this.normalizeValue(this.stringValueControl.value);
        this.currentQuery = query;

        this.fetchSearchResults(query, true);
    }

    public handleBlur(): void {
        this.isInputFocused = false;
    }

    public handleClick(): void {
        this.isInputFocused = true;

        if (this.loading) {
            return;
        }

        if (this.searchResults.length > 0 || this.noResults) {
            this.openPanelIfNeeded();
            return;
        }

        this.fetchSearchResults(this.normalizeValue(this.stringValueControl.value), true);
    }

    private normalizeValue(value: any): string {
        if (typeof value === 'string') {
            return value.trim();
        }

        if (value === null || value === undefined) {
            return '';
        }

        return `${value}`.trim();
    }

    private prioritizeResults(results: string[], query: string): string[] {
        const normalizedQuery = this.normalizeValue(query).toLowerCase();
        if (!normalizedQuery) {
            return results;
        }

        return [...results].sort((left, right) => {
            const leftScore = this.getMatchScore(left, normalizedQuery);
            const rightScore = this.getMatchScore(right, normalizedQuery);

            if (leftScore !== rightScore) {
                return leftScore - rightScore;
            }

            return left.localeCompare(right);
        });
    }

    private getMatchScore(value: string, normalizedQuery: string): number {
        const normalizedValue = this.normalizeValue(value).toLowerCase();

        if (normalizedValue === normalizedQuery) {
            return 0;
        }

        if (normalizedValue.startsWith(normalizedQuery)) {
            return 1;
        }

        if (normalizedValue.indexOf(normalizedQuery) >= 0) {
            return 2;
        }

        return 3;
    }

    private filterResults(results: string[], query: string): string[] {
        const normalizedQuery = this.normalizeValue(query).toLowerCase();
        if (!normalizedQuery) {
            return results || [];
        }

        return (results || []).filter(item => this.normalizeValue(item).toLowerCase().indexOf(normalizedQuery) >= 0);
    }

    private openPanelIfNeeded(): void {
        if (!this.isInputFocused || !this.matAutocompleteTrigger) {
            return;
        }

        if (!(this.searchResults.length > 0 || this.canCreateCurrentValue || this.noResults)) {
            return;
        }

        setTimeout(() => {
            if (!this.isInputFocused || !this.matAutocompleteTrigger) {
                return;
            }

            this.matAutocompleteTrigger.openPanel();
            this.matAutocompleteTrigger.updatePosition();
        });
    }

    private fetchSearchResults(query: string, openPanel: boolean = false): void {
        this.loading = true;
        this.noResults = false;

        this.data.search(this.sourceList, query)
            .pipe(catchError(() => of([])))
            .subscribe((results: string[]) => {
                this.loading = false;
                this.searchResults = this.prioritizeResults(this.filterResults(results || [], query), query);
                this.noResults = !!query && this.searchResults.length === 0;

                if (openPanel) {
                    this.openPanelIfNeeded();
                }
            });
    }

    ngAfterViewInit() {
        this.sourceList = this.controllerName;

        const typeahead = this.stringValueControl.valueChanges.pipe(
            map((value: any) => this.normalizeValue(value)),
            tap((query: string) => {
                this.currentQuery = query;

                if (!query) {
                    this.activeItem = null;
                    this.searchResults = [];
                    this.noResults = false;
                    this.lastCommittedValue = '';
                    return;
                }

                if (query !== this.lastCommittedValue) {
                    this.lastCommittedValue = '';
                }

                if (query !== this.activeItem) {
                    this.activeItem = null;
                }
            }),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((query: string) => {
                this.loading = true;
                return this.data.search(this.sourceList, query)
                    .pipe(
                        map((results: string[]) => ({ query, results: results || [] })),
                        catchError(() => of({ query, results: [] }))
                    );
            })
        );

        typeahead.subscribe(data => {
            this.loading = false;
            this.currentQuery = data.query;
            this.searchResults = this.prioritizeResults(this.filterResults(<string[]>data.results, data.query), data.query);
            this.noResults = !!data.query && this.searchResults.length === 0;

            if (this.isInputFocused) {
                this.openPanelIfNeeded();
            }
        });
    }

}

